// utils/offlineStorage/circuit.ts - utils/offlineStorage.ts split (sub-module circuit)



/**
 * Circuit Breaker Pattern
 * Prevents overwhelming Supabase when it's down
 * Trips open after consecutive failures, closes after recovery
 */
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  
  constructor(
    private failureThreshold = 5,        // Open after 5 consecutive failures
    private recoveryTimeout = 30000,      // Try again after 30s
    private successThreshold = 2          // Close after 2 consecutive successes
  ) {}

  /**
   * Check if circuit breaker allows request
   */
  canExecute(): boolean {
    if (this.state === 'closed') return true;
    
    if (this.state === 'open') {
      // Check if recovery timeout has passed
      if (Date.now() - this.lastFailureTime >= this.recoveryTimeout) {
        console.log('🔄 Circuit breaker: Attempting recovery (half-open)');
        this.state = 'half-open';
        return true;
      }
      console.warn('⚠️ Circuit breaker OPEN - blocking request to prevent overload');
      return false;
    }
    
    // half-open state - allow request to test recovery
    return true;
  }

  /**
   * Record successful request
   */
  recordSuccess(): void {
    if (this.state === 'half-open') {
      this.failureCount = 0;
      this.state = 'closed';
      console.log('✅ Circuit breaker CLOSED - service recovered');
    } else if (this.state === 'closed') {
      this.failureCount = 0; // Reset on success
    }
  }

  /**
   * Record failed request
   */
  recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'open';
      console.error(`🚨 Circuit breaker OPEN after ${this.failureCount} failures`);
      console.error('⚠️ Blocking requests for', this.recoveryTimeout / 1000, 'seconds');
    }
  }

  /**
   * Get current circuit state
   */
  getState(): { state: string; failureCount: number } {
    return {
      state: this.state,
      failureCount: this.failureCount
    };
  }

  /**
   * Manually reset circuit breaker
   */
  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.lastFailureTime = 0;
    console.log('🔄 Circuit breaker manually reset');
  }
}



// Global circuit breaker instance
export const circuitBreaker = new CircuitBreaker();
